import { Injectable, inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import { AuthStore } from '@core/services/auth/auth.store';
import { isCockpitDemoMode } from './cockpit-demo-mode';
import type { CockpitMockStore } from './cockpit-mock-store.service';

/**
 * Holds the outcome of the one-time demo-mode decision made by
 * {@link cockpitMockStorePreloadResolver}, and — only when demo mode is on —
 * the dynamically-imported `CockpitMockStore` constructor.
 *
 * `providedIn: 'root'` so the same instance is reachable both from the
 * resolver (which runs in the PARENT route's injection context, before the
 * cockpit route's own `providers` exist) and from `provideCockpitStore()`'s
 * `COCKPIT_STORE` factory (which runs inside that route's injector).
 */
@Injectable({ providedIn: 'root' })
export class CockpitDemoPreload {
  demoMode = false;
  mockStoreCtor: typeof CockpitMockStore | null = null;
}

/**
 * Route resolver for the cockpit routes: decides demo mode exactly once and,
 * only then, `import()`s `CockpitMockStore` — which pulls in `SEED_CARDS`
 * (~2000 lines of demo fixtures) — before the route's `providers` activate.
 *
 * #513 point 55 — Angular provider factories are synchronous, so without
 * this the mock store had to be imported statically at the top of
 * `cockpit-store.provider.ts`, shipping into every production bundle even
 * though `isCockpitDemoMode()` always returns `false` there: the
 * `environment.production` guard is a runtime check, invisible to the
 * bundler, which cannot prove the branch is dead without running it.
 *
 * Always resolves `true` — nothing gates navigation on this outcome, the
 * resolver exists purely for its `import()` side effect, run once ahead of
 * `COCKPIT_STORE`'s factory instead of inside it.
 */
export const cockpitMockStorePreloadResolver: ResolveFn<boolean> = async () => {
  const preload = inject(CockpitDemoPreload);
  preload.demoMode = isCockpitDemoMode(inject(AuthStore).user()?.userUID ?? null);
  if (preload.demoMode && !preload.mockStoreCtor) {
    const { CockpitMockStore } = await import('./cockpit-mock-store.service');
    preload.mockStoreCtor = CockpitMockStore;
  }
  return true;
};
